// eslint-disable-next-line import/no-cycle
import { adminApi } from '@/includes/contentManagementApi';

/**
 * DevTips Service
 * Handles all API calls for development tips management
 */
const devTipsService = {
  /**
   * Get all development tips with optional filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Paginated list of tips
   */
  async getAllTips(params = {}) {
    const response = await adminApi.get('/dev-tips', { params });
    return response.data;
  },

  /**
   * Get a specific development tip by ID
   * @param {string} id - Tip ID
   * @returns {Promise<Object>} Development tip
   */
  async getTipById(id) {
    const response = await adminApi.get(`/dev-tips/${id}`);
    return response.data;
  },

  /**
   * Get tips by dimension value
   * @param {string} dimensionValue - Dimension value
   * @returns {Promise<Array>} Array of tips
   */
  async getTipsByDimension(dimensionValue) {
    const response = await adminApi.get(`/dev-tips/dimension/${dimensionValue}`);
    return response.data;
  },

  /**
   * Get random tip for a dimension
   * @param {string} dimensionValue - Dimension value
   * @returns {Promise<Object|null>} Random tip or null
   */
  async getRandomTipForDimension(dimensionValue) {
    const response = await adminApi.get(`/dev-tips/random/${dimensionValue}`);
    return response.data;
  },

  /**
   * Get tips for multiple dimensions
   * @param {Array<string>} dimensionValues - Array of dimension values
   * @returns {Promise<Array>} Array of tips
   */
  async getTipsForMultipleDimensions(dimensionValues) {
    const response = await adminApi.post('/dev-tips/multiple-dimensions', {
      dimensionValues,
    });
    return response.data;
  },

  /**
   * Create a new development tip
   * @param {Object} tipData - Tip data
   * @returns {Promise<Object>} Created tip
   */
  async createTip(tipData) {
    const response = await adminApi.post('/dev-tips', tipData);
    return response.data;
  },

  /**
   * Update a development tip
   * @param {string} id - Tip ID
   * @param {Object} tipData - Updated tip data
   * @returns {Promise<Object>} Updated tip
   */
  async updateTip(id, tipData) {
    const response = await adminApi.patch(`/dev-tips/${id}`, tipData);
    return response.data;
  },

  /**
   * Toggle tip status
   * @param {string} id - Tip ID
   * @param {boolean} active - New active status
   * @returns {Promise<Object>} Updated tip
   */
  async toggleTipStatus(id, active) {
    const response = await adminApi.patch(`/dev-tips/${id}/status`, { active });
    return response.data;
  },

  /**
   * Delete a development tip
   * @param {string} id - Tip ID
   * @returns {Promise<void>}
   */
  async deleteTip(id) {
    await adminApi.delete(`/dev-tips/${id}`);
  },

  /**
   * Bulk import development tips
   * @param {Object} bulkData - Bulk import data
   * @returns {Promise<Object>} Bulk operation result
   */
  async bulkImport(bulkData) {
    const response = await adminApi.post('/dev-tips/bulk/import', bulkData);
    return response.data;
  },

  /**
   * Chunked bulk import development tips
   * @param {Object} chunkedBulkData - Chunked bulk import data
   * @returns {Promise<Object>} Chunked bulk operation result
   */
  async chunkedBulkImport(chunkedBulkData) {
    const response = await adminApi.post('/dev-tips/bulk-import-chunked', chunkedBulkData);
    return response.data;
  },

  /**
   * Bulk update development tips
   * @param {Object} bulkData - Bulk update data
   * @returns {Promise<Object>} Bulk operation result
   */
  async bulkUpdate(bulkData) {
    const response = await adminApi.patch('/dev-tips/bulk/update', bulkData);
    return response.data;
  },

  /**
   * Bulk delete development tips
   * @param {Object} bulkData - Bulk delete data
   * @returns {Promise<Object>} Bulk operation result
   */
  async bulkDelete(bulkData) {
    const response = await adminApi.delete('/dev-tips/bulk/delete', {
      data: bulkData,
    });
    return response.data;
  },

  /**
   * Bulk toggle status of development tips
   * @param {Object} bulkData - Bulk toggle data
   * @returns {Promise<Object>} Bulk operation result
   */
  async bulkToggleStatus(bulkData) {
    const response = await adminApi.patch('/dev-tips/bulk/toggle-status', bulkData);
    return response.data;
  },

  /**
   * Get development tips statistics
   * @returns {Promise<Object>} Statistics data
   */
  async getStatistics() {
    const response = await adminApi.get('/dev-tips/statistics/overview');
    return response.data;
  },

  // ===== USER-SPECIFIC DEVTIPS METHODS =====

  /**
   * Get user's DevTip library
   * @returns {Promise<Object>} User's DevTip library
   */
  async getUserDevTipLibrary() {
    const response = await adminApi.get('/user/devtip-library');
    return response.data;
  },

  /**
   * Mark a DevTip as viewed by the user
   * @param {string} tipId - The DevTip ID to mark as viewed
   * @returns {Promise<Object>} Mark as viewed result
   */
  async markDevTipAsViewed(tipId) {
    // Validate tipId before making the request
    if (!tipId) {
      throw new Error('tipId is required');
    }

    if (typeof tipId !== 'string') {
      throw new Error('tipId must be a string');
    }

    // Check if it looks like a MongoDB ObjectId (24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(tipId)) {
      throw new Error(`Invalid tipId format: ${tipId}. Expected MongoDB ObjectId format.`);
    }

    const response = await adminApi.post(`/user/devtip-viewed/${tipId}`);
    return response.data;
  },

  /**
   * Submit feedback for a DevTip
   * @param {Object} feedbackData - Feedback data containing id, feedback, and status_done
   * @returns {Promise<Object>} Feedback submission result
   */
  async submitDevTipFeedback(feedbackData) {
    const response = await adminApi.post('/user/devtip-feedback', feedbackData);
    return response.data;
  },

  // ===== ADMIN DEVTIPS METHODS =====

  /**
   * Admin: Send DevTips to specific users
   * @param {Array<string>} userIds - Array of user IDs to send DevTips to
   * @param {string} schemaId - Schema ID to get DevTips from
   * @returns {Promise<Object>} DevTip sending result
   */
  async sendDevTipToUsers(userIds, schemaId) {
    const response = await adminApi.post('/schemas/admin/send-devtip', {
      userIds,
      schemaId,
    });
    return response.data;
  },
};

export default devTipsService;
